import { useState, useEffect } from 'react';
import { Sidebar, useSidebar } from '@/components/Sidebar';
import { useSearchParams } from 'react-router-dom';
import { useAuth, useTheme, useCurrency, CURRENCIES, Currency } from '@/contexts';
import { useExchangeRates } from '@/hooks/useExchangeRates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import TwoFactorSetupModal from '@/components/TwoFactorSetupModal';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import {
  User,
  Bell,
  Shield,
  Palette,
  Globe,
  Zap,
  Moon,
  Sun,
  Check,
  Lock,
  Camera,
  Trash2,
  Download,
  Upload,
  Smartphone,
  LogOut,
  Loader2,
  Monitor,
  Tablet,
  Coins,
  Tag,
  AlertTriangle,
  Power,
  Wallet,
  RotateCcw,
  RefreshCw,
  Trophy,
} from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import CategorySettings from '@/components/CategorySettings';
import AchievementsSettings from '@/components/AchievementsSettings';
import { useExplorationAchievement } from '@/hooks/useAchievementTriggers';
import { useAchievementsContext } from '@/contexts/AchievementsContext';
import { useAccounts } from '@/hooks/useAccounts';
import { DisableBankSyncModal } from '@/components/DisableBankSyncModal';

interface Session {
  id: string;
  user_agent: string;
  ip: string;
  created_at: string;
  updated_at: string;
  is_current: boolean;
}

export default function SettingsPage() {
  const { collapsed: sidebarCollapsed } = useSidebar();
  useExplorationAchievement('explore_settings');
  const { checkAndUnlock } = useAchievementsContext();
  const { user, updateProfile, logout } = useAuth();
  const { t, language } = useTranslation();
  const { toast } = useToast();
  const { isDarkMode, setDarkMode } = useTheme();
  const { currency, setCurrency } = useCurrency();
  const { rates: exchangeRates, updatedAt: ratesUpdatedAt } = useExchangeRates();
  const { accounts, updateAccount, loadAccounts } = useAccounts();
  const [isDisablingBankSync, setIsDisablingBankSync] = useState(false);
  const syncedAccounts = accounts.filter((account) => account.source === 'powens');
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return tabFromUrl &&
      [
        'profile',
        'categories',
        'notifications',
        'security',
        'appearance',
        'billing',
        'achievements',
        'currency',
      ].includes(tabFromUrl)
      ? tabFromUrl
      : 'profile';
  });

  useEffect(() => {
    if (activeTab !== 'categories' || window.location.hash !== '#guide-categories') return;
    const focusTimer = window.setTimeout(() => {
      document.getElementById('guide-categories')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 50);
    return () => window.clearTimeout(focusTimer);
  }, [activeTab]);
  const [is2FAModalOpen, setIs2FAModalOpen] = useState(false);
  const [has2FAEnabled, setHas2FAEnabled] = useState(false);
  const [notifications, setNotifications] = useState({
    email: true,
    push: false,
    transactions: true,
    budgets: true,
    weekly: false,
  });
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  // Profile state
  const [profileName, setProfileName] = useState(user?.name || '');
  const [profilePhone, setProfilePhone] = useState('');
  const [profileAddress, setProfileAddress] = useState('');
  const [profileAvatar, setProfileAvatar] = useState<string | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);

  // Load profile data from Supabase user metadata
  useEffect(() => {
    const loadProfileData = async () => {
      const {
        data: { user: supaUser },
      } = await supabase.auth.getUser();
      if (supaUser?.user_metadata) {
        setProfileName(supaUser.user_metadata.name || '');
        setProfilePhone(supaUser.user_metadata.phone || '');
        setProfileAddress(supaUser.user_metadata.address || '');
        setProfileAvatar(supaUser.user_metadata.avatar_url || null);
        // Load notification preferences
        if (supaUser.user_metadata.notifications) {
          setNotifications(supaUser.user_metadata.notifications);
        }
      }
    };
    loadProfileData();
  }, []);

  // Update URL when tab changes
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSearchParams(tab !== 'profile' ? { tab } : {});
  };

  // Handle notification toggle and save to Supabase
  const handleNotificationToggle = async (key: keyof typeof notifications) => {
    const newNotifications = {
      ...notifications,
      [key]: !notifications[key],
    };
    setNotifications(newNotifications);

    try {
      setNotificationsLoading(true);
      const { error } = await supabase.auth.updateUser({
        data: { notifications: newNotifications },
      });

      if (error) throw error;

      toast({
        title: t('settings.notifications.saved'),
        description: t('settings.notifications.savedDesc'),
      });
    } catch (err) {
      console.error('Error saving notifications:', err);
      // Revert on error
      setNotifications(notifications);
      toast({
        title: t('settings.notifications.error'),
        description: t('settings.notifications.errorDesc'),
        variant: 'destructive',
      });
    } finally {
      setNotificationsLoading(false);
    }
  };

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Active sessions state
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [revokingSessionId, setRevokingSessionId] = useState<string | null>(null);

  // Check 2FA status on mount
  useEffect(() => {
    const check2FAStatus = async () => {
      try {
        const { data } = await supabase.auth.mfa.listFactors();
        const verifiedFactors = data?.totp.filter((f) => f.status === 'verified') || [];
        setHas2FAEnabled(verifiedFactors.length > 0);
      } catch (err) {
        console.error('Error checking 2FA status:', err);
      }
    };
    check2FAStatus();
  }, []);

  // Fetch active sessions
  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setSessionsLoading(true);
        const { data: sessionData } = await supabase.auth.getSession();

        // Get current session info
        const userAgent = navigator.userAgent;
        const expiresAt = sessionData.session?.expires_at;
        const currentSession: Session = {
          id: 'current',
          user_agent: userAgent,
          ip: '',
          created_at: expiresAt
            ? new Date(expiresAt * 1000 - 3600000).toISOString()
            : new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_current: true,
        };

        setSessions([currentSession]);
      } catch (err) {
        console.error('Error fetching sessions:', err);
      } finally {
        setSessionsLoading(false);
      }
    };
    fetchSessions();
  }, []);

  // Parse user agent to get device info
  const parseUserAgent = (ua: string) => {
    let browser = 'Unknown Browser';
    let os = 'Unknown OS';

    // Detect browser
    if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';

    // Detect OS
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac OS X') || ua.includes('Macintosh')) os = 'MacOS';
    else if (ua.includes('Linux') && !ua.includes('Android')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

    return { browser, os, display: `${browser} ${t('settings.security.onDevice')} ${os}` };
  };

  // Get device icon
  const getDeviceIcon = (ua: string) => {
    if (ua.includes('Mobile') || ua.includes('Android') || ua.includes('iPhone')) {
      return Smartphone;
    }
    if (ua.includes('iPad') || ua.includes('Tablet')) {
      return Tablet;
    }
    return Monitor;
  };

  // Handle password change
  const handlePasswordChange = async () => {
    setPasswordError('');

    // Validate inputs
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(t('settings.security.allFieldsRequired'));
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError(t('settings.security.passwordMinLength'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t('settings.security.passwordsNoMatch'));
      return;
    }

    if (!user?.email) {
      setPasswordError(t('settings.security.passwordUpdateError'));
      return;
    }

    try {
      setPasswordLoading(true);

      // First verify the current password by trying to sign in
      // We use a separate auth check to validate the current password
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (verifyError) {
        setPasswordError(t('settings.security.currentPasswordWrong'));
        return;
      }

      // Now update to the new password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        setPasswordError(updateError.message);
        return;
      }

      // Clear form and show success
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast({
        title: t('settings.security.passwordUpdated'),
        description: t('settings.security.passwordUpdatedDesc'),
      });
    } catch (err) {
      console.error('Error updating password:', err);
      setPasswordError(t('settings.security.passwordUpdateError'));
    } finally {
      setPasswordLoading(false);
    }
  };

  // Handle profile save
  const handleProfileSave = async () => {
    try {
      setProfileLoading(true);
      await updateProfile({
        name: profileName,
        phone: profilePhone,
        address: profileAddress,
      });
      toast({
        title: t('settings.profile.saved'),
        description: t('settings.profile.savedDesc'),
      });
      // Trigger achievement if profile fields are filled
      if (profileName && profilePhone) {
        checkAndUnlock('profile_completed');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      toast({
        title: t('settings.profile.error'),
        description: t('settings.profile.errorDesc'),
        variant: 'destructive',
      });
    } finally {
      setProfileLoading(false);
    }
  };

  // Handle photo upload
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
      toast({
        title: t('settings.profile.invalidFileType'),
        description: t('settings.profile.invalidFileTypeDesc'),
        variant: 'destructive',
      });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: t('settings.profile.fileTooLarge'),
        description: t('settings.profile.fileTooLargeDesc'),
        variant: 'destructive',
      });
      return;
    }

    try {
      setAvatarUploading(true);

      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user?.id}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const {
        data: { publicUrl },
      } = supabase.storage.from('avatars').getPublicUrl(filePath);

      // Update user metadata
      const { error: updateError } = await supabase.auth.updateUser({
        data: { avatar_url: publicUrl },
      });

      if (updateError) throw updateError;

      setProfileAvatar(publicUrl);
      toast({
        title: t('settings.profile.photoUploaded'),
        description: t('settings.profile.photoUploadedDesc'),
      });
    } catch (err) {
      console.error('Error uploading photo:', err);
      toast({
        title: t('settings.profile.uploadError'),
        description: t('settings.profile.uploadErrorDesc'),
        variant: 'destructive',
      });
    } finally {
      setAvatarUploading(false);
    }
  };

  // Handle photo delete
  const handlePhotoDelete = async () => {
    try {
      setAvatarUploading(true);

      // Update user metadata to remove avatar
      const { error } = await supabase.auth.updateUser({
        data: { avatar_url: null },
      });

      if (error) throw error;

      setProfileAvatar(null);
      toast({
        title: t('settings.profile.photoDeleted'),
        description: t('settings.profile.photoDeletedDesc'),
      });
    } catch (err) {
      console.error('Error deleting photo:', err);
      toast({
        title: t('settings.profile.deleteError'),
        description: t('settings.profile.deleteErrorDesc'),
        variant: 'destructive',
      });
    } finally {
      setAvatarUploading(false);
    }
  };

  // Handle session revocation
  const handleRevokeSession = async (sessionId: string) => {
    if (sessionId === 'current') {
      // Cannot revoke current session from here
      return;
    }

    try {
      setRevokingSessionId(sessionId);
      // Sign out from specific session (Supabase doesn't support this directly)
      // For now, we'll just show a message
      toast({
        title: t('settings.security.sessionRevoked'),
        description: t('settings.security.sessionRevokedDesc'),
      });
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } catch (err) {
      console.error('Error revoking session:', err);
    } finally {
      setRevokingSessionId(null);
    }
  };

  // Handle sign out all other sessions
  const handleSignOutOthers = async () => {
    try {
      // Supabase provides signOut with scope: 'others'
      await supabase.auth.signOut({ scope: 'others' });
      setSessions((prev) => prev.filter((s) => s.is_current));
      toast({
        title: t('settings.security.otherSessionsRevoked'),
        description: t('settings.security.otherSessionsRevokedDesc'),
      });
    } catch (err) {
      console.error('Error signing out other sessions:', err);
    }
  };

  // Account management state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [accountActionLoading, setAccountActionLoading] = useState(false);

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'SUPPRIMER') return;
    try {
      setAccountActionLoading(true);
      // Call RPC that bans the user and sets deletion metadata
      const { error } = await supabase.rpc('request_account_deletion');
      if (error) throw error;
      toast({
        title: t('settings.account.deleteRequested'),
        description: t('settings.account.deleteRequestedDesc'),
      });
      // Sign out and redirect to landing page
      setTimeout(async () => {
        try {
          await logout();
        } catch {
          /* session already invalid after ban */
        }
        // Clear all storage to prevent stale banned session on reload
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/';
      }, 2000);
    } catch (err) {
      console.error('Error requesting account deletion:', err);
      toast({
        title: t('settings.account.error'),
        description: t('settings.account.errorDesc'),
        variant: 'destructive',
      });
    } finally {
      setAccountActionLoading(false);
    }
  };

  const tabGroups = [
    {
      label: language === 'fr' ? 'Général' : 'General',
      tabs: [
        { id: 'profile', label: t('settings.tab.profile'), icon: User },
        { id: 'notifications', label: t('settings.tab.notifications'), icon: Bell },
        { id: 'security', label: t('settings.tab.security'), icon: Shield },
        { id: 'appearance', label: t('settings.tab.appearance'), icon: Palette },
        { id: 'account', label: t('settings.tab.account'), icon: Power },
      ],
    },
    {
      label: language === 'fr' ? 'Paramétrage financier' : 'Financial Settings',
      tabs: [
        { id: 'currency', label: language === 'fr' ? 'Devise' : 'Currency', icon: Coins },
        { id: 'categories', label: t('settings.tab.categories'), icon: Tag },
        {
          id: 'accounts',
          label: language === 'fr' ? 'Gestion des comptes' : 'Account Management',
          icon: Wallet,
        },
      ],
    },
    {
      label: language === 'fr' ? 'Parcours clevia' : 'clevia Journey',
      tabs: [
        {
          id: 'achievements',
          label: language === 'fr' ? 'Succès' : 'Achievements',
          icon: Trophy,
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#f9fafb] dark:bg-[#0f1117]">
      <Sidebar activePage="settings" />
      <main className={`${sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-64'}`}>
        {/* Header */}
        <header className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md border-b border-gray-100 dark:border-gray-700/50 sticky top-0 z-40">
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t('settings.title')}
              </h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
                {t('settings.subtitle')}
              </p>
            </div>
          </div>
        </header>

        <div className="p-6">
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Settings Navigation */}
            <div className="lg:col-span-1">
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-3 sticky top-24">
                <nav className="space-y-4">
                  {tabGroups.map((group, groupIdx) => (
                    <div key={group.label}>
                      {groupIdx > 0 && (
                        <div className="border-t border-gray-100 dark:border-gray-700/50 -mx-3 mb-3" />
                      )}
                      <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 mb-1.5">
                        {group.label}
                      </p>
                      <div className="space-y-1">
                        {group.tabs.map((tab) => {
                          const Icon = tab.icon;
                          const isActive = activeTab === tab.id;
                          return (
                            <button
                              key={tab.id}
                              onClick={() => handleTabChange(tab.id)}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                                isActive
                                  ? 'bg-[#16c47f]/10 text-[#16c47f]'
                                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/80 dark:hover:bg-gray-700/50'
                              }`}
                            >
                              <Icon
                                className={`w-[18px] h-[18px] ${isActive ? 'text-[#16c47f]' : 'text-gray-400 dark:text-gray-500'}`}
                              />
                              <span>{tab.label}</span>
                              {isActive && (
                                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#16c47f]" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </nav>
              </div>
            </div>

            {/* Content Area */}
            <div className="lg:col-span-3">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="space-y-6">
                  {/* Avatar Section */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-5 flex items-center gap-2">
                      <User className="w-5 h-5 text-[#16c47f]" />
                      {t('settings.profile.photo')}
                    </h2>
                    <div className="flex items-center gap-6">
                      <div className="relative group">
                        {profileAvatar ? (
                          <img
                            src={profileAvatar}
                            alt="Profile"
                            className="w-20 h-20 rounded-full object-cover shadow-sm"
                          />
                        ) : (
                          <div className="w-20 h-20 bg-[#16c47f] rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-sm">
                            {profileName?.charAt(0).toUpperCase() ||
                              user?.email?.charAt(0).toUpperCase() ||
                              'U'}
                          </div>
                        )}
                        <label className="absolute bottom-0 right-0 w-7 h-7 bg-white dark:bg-gray-700 rounded-full flex items-center justify-center border border-gray-200 dark:border-gray-600 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition cursor-pointer">
                          <Camera className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" />
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/gif"
                            onChange={handlePhotoUpload}
                            className="hidden"
                          />
                        </label>
                      </div>
                      <div className="flex-1 space-y-3">
                        <div className="flex gap-3">
                          <Button
                            className="bg-[#16c47f] hover:bg-[#12b070] text-white font-medium relative"
                            disabled={avatarUploading}
                          >
                            {avatarUploading ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-2" />
                            )}
                            {t('settings.profile.upload')}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/gif"
                              onChange={handlePhotoUpload}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              disabled={avatarUploading}
                            />
                          </Button>
                          <Button
                            variant="outline"
                            onClick={handlePhotoDelete}
                            disabled={avatarUploading || !profileAvatar}
                            className="border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            {t('settings.profile.delete')}
                          </Button>
                        </div>
                        <p className="text-gray-400 dark:text-gray-500 text-sm">
                          {t('settings.profile.photoDesc')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Personal Info */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">
                      {t('settings.profile.personalInfo')}
                    </h2>
                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-gray-700 dark:text-gray-300 text-sm font-medium">
                            {t('settings.profile.fullName')}
                          </Label>
                          <Input
                            value={profileName}
                            onChange={(e) => setProfileName(e.target.value)}
                            className="bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-[#16c47f] focus:ring-[#16c47f]/20 rounded-xl h-11"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-700 dark:text-gray-300 text-sm font-medium">
                            {t('settings.profile.email')}
                          </Label>
                          <Input
                            defaultValue={user?.email || ''}
                            disabled
                            className="bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500 rounded-xl h-11"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700 dark:text-gray-300 text-sm font-medium">
                          {t('settings.profile.phone')}
                        </Label>
                        <Input
                          value={profilePhone}
                          onChange={(e) => setProfilePhone(e.target.value)}
                          placeholder={t('settings.profile.phonePlaceholder')}
                          className="bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-400 focus:border-[#16c47f] focus:ring-[#16c47f]/20 rounded-xl h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700 dark:text-gray-300 text-sm font-medium">
                          {t('settings.profile.address')}
                        </Label>
                        <Input
                          value={profileAddress}
                          onChange={(e) => setProfileAddress(e.target.value)}
                          placeholder={t('settings.profile.addressPlaceholder')}
                          className="bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-400 focus:border-[#16c47f] focus:ring-[#16c47f]/20 rounded-xl h-11"
                        />
                      </div>
                      <Button
                        onClick={handleProfileSave}
                        disabled={profileLoading}
                        className="mt-4 bg-[#16c47f] hover:bg-[#12b070] text-white font-medium"
                      >
                        {profileLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4 mr-2" />
                        )}
                        {t('settings.profile.save')}
                      </Button>
                    </div>
                  </div>

                  {/* Profile Completion Card */}
                  {(() => {
                    const fields = [
                      {
                        done: !!profileName,
                        label: language === 'fr' ? 'Nom complet' : 'Full name',
                      },
                      {
                        done: !!profileAvatar,
                        label: language === 'fr' ? 'Photo de profil' : 'Profile photo',
                      },
                      { done: !!profilePhone, label: language === 'fr' ? 'Téléphone' : 'Phone' },
                      { done: !!profileAddress, label: language === 'fr' ? 'Adresse' : 'Address' },
                    ];
                    const completed = fields.filter((f) => f.done).length;
                    const total = fields.length;
                    const allDone = completed === total;

                    if (allDone) return null;

                    return (
                      <div className="bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/15 rounded-2xl border border-emerald-100 dark:border-emerald-800/30 p-5">
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-xl bg-[#16c47f]/15 flex items-center justify-center flex-shrink-0">
                            <Trophy className="w-4.5 h-4.5 text-[#16c47f]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                              {language === 'fr'
                                ? 'Complète ton profil pour débloquer le succès 🏆'
                                : 'Complete your profile to unlock the achievement 🏆'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {language === 'fr'
                                ? `${completed}/${total} infos renseignées`
                                : `${completed}/${total} fields completed`}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-3">
                              {fields.map((f) => (
                                <span
                                  key={f.label}
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium ${
                                    f.done
                                      ? 'bg-white/70 dark:bg-gray-800/50 text-emerald-600 dark:text-emerald-400'
                                      : 'bg-white/50 dark:bg-gray-800/30 text-gray-500 dark:text-gray-400'
                                  }`}
                                >
                                  {f.done ? (
                                    <Check className="w-3 h-3" />
                                  ) : (
                                    <span className="w-3 h-3 rounded-full border border-gray-300 dark:border-gray-600 inline-block" />
                                  )}
                                  {f.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Categories Tab */}
              {activeTab === 'categories' && (
                <div
                  id="guide-categories"
                  className="scroll-mt-24 target:ring-2 target:ring-[#16c47f]/40 target:ring-offset-4 target:ring-offset-[#f9fafb] dark:target:ring-offset-[#0f1117]"
                >
                  <CategorySettings />
                </div>
              )}

              {/* Account Management Tab */}
              {activeTab === 'accounts' && (
                <div className="space-y-6">
                  {/* Only shown when there is something to turn off: a user with
                      no connected bank has no decision to make here. */}
                  {syncedAccounts.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                        <RefreshCw className="w-5 h-5 text-[#16c47f]" />
                        {t('bankSync.settingsTitle')}
                      </h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
                        {t(
                          syncedAccounts.length === 1
                            ? 'bankSync.settingsDescOne'
                            : 'bankSync.settingsDesc'
                        ).replace('{count}', String(syncedAccounts.length))}
                      </p>
                      <Button variant="outline" onClick={() => setIsDisablingBankSync(true)}>
                        {t('bankSync.disable')}
                      </Button>
                    </div>
                  )}

                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-[#16c47f]" />
                      {language === 'fr' ? 'Comptes archivés' : 'Archived Accounts'}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
                      {language === 'fr'
                        ? 'Les comptes supprimés sont archivés ici. Vous pouvez les réactiver à tout moment.'
                        : 'Deleted accounts are archived here. You can reactivate them at any time.'}
                    </p>

                    {(() => {
                      const archivedAccounts = accounts.filter(
                        (a) =>
                          a.is_hidden && (a.metadata as Record<string, unknown> | null)?.archived
                      );
                      if (archivedAccounts.length === 0) {
                        return (
                          <div className="text-center py-10 text-gray-400 dark:text-gray-500">
                            <Wallet className="w-10 h-10 mx-auto mb-3 opacity-30" />
                            <p className="text-sm">
                              {language === 'fr'
                                ? 'Aucun compte archivé.'
                                : 'No archived accounts.'}
                            </p>
                          </div>
                        );
                      }
                      return (
                        <div className="space-y-3">
                          {archivedAccounts.map((account) => (
                            <div
                              key={account.id}
                              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/40 rounded-xl"
                            >
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {account.name}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                  {account.subtype} &middot; {account.institution || '—'}
                                </p>
                              </div>
                              <button
                                onClick={async () => {
                                  const { archived, ...restMetadata } =
                                    (account.metadata as Record<string, unknown>) || {};
                                  await updateAccount(account.id, {
                                    is_hidden: false,
                                    metadata:
                                      Object.keys(restMetadata).length > 0 ? restMetadata : null,
                                  });
                                  toast({
                                    title:
                                      language === 'fr' ? 'Compte réactivé' : 'Account reactivated',
                                    description:
                                      language === 'fr'
                                        ? `"${account.name}" est de nouveau actif.`
                                        : `"${account.name}" is active again.`,
                                  });
                                }}
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#16c47f] bg-[#16c47f]/10 hover:bg-[#16c47f]/20 rounded-lg transition-colors flex-shrink-0"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                                {language === 'fr' ? 'Réactiver' : 'Reactivate'}
                              </button>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <div className="space-y-6">
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-5 flex items-center gap-2">
                      <Bell className="w-5 h-5 text-[#16c47f]" />
                      {t('settings.notifications.title')}
                    </h2>
                    <div className="space-y-4">
                      {[
                        {
                          id: 'email',
                          label: t('settings.notifications.email'),
                          desc: t('settings.notifications.emailDesc'),
                        },
                        {
                          id: 'push',
                          label: t('settings.notifications.push'),
                          desc: t('settings.notifications.pushDesc'),
                        },
                        {
                          id: 'transactions',
                          label: t('settings.notifications.transactions'),
                          desc: t('settings.notifications.transactionsDesc'),
                        },
                        {
                          id: 'budgets',
                          label: t('settings.notifications.budgets'),
                          desc: t('settings.notifications.budgetsDesc'),
                        },
                        {
                          id: 'weekly',
                          label: t('settings.notifications.weekly'),
                          desc: t('settings.notifications.weeklyDesc'),
                        },
                      ].map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        >
                          <div>
                            <p className="text-gray-900 dark:text-white font-medium text-sm">
                              {item.label}
                            </p>
                            <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">
                              {item.desc}
                            </p>
                          </div>
                          <button
                            onClick={() =>
                              handleNotificationToggle(item.id as keyof typeof notifications)
                            }
                            disabled={notificationsLoading}
                            className={`relative w-12 h-6 rounded-full transition-all duration-200 ${
                              notifications[item.id as keyof typeof notifications]
                                ? 'bg-[#16c47f]'
                                : 'bg-gray-300 dark:bg-gray-600'
                            } ${notificationsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <div
                              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                                notifications[item.id as keyof typeof notifications]
                                  ? 'translate-x-6'
                                  : ''
                              }`}
                            />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div className="space-y-6">
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-5 flex items-center gap-2">
                      <Lock className="w-5 h-5 text-[#16c47f]" />
                      {t('settings.security.changePassword')}
                    </h2>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-gray-700 dark:text-gray-300 text-sm font-medium">
                          {t('settings.security.currentPassword')}
                        </Label>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-[#16c47f] focus:ring-[#16c47f]/20 rounded-xl h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700 dark:text-gray-300 text-sm font-medium">
                          {t('settings.security.newPassword')}
                        </Label>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-[#16c47f] focus:ring-[#16c47f]/20 rounded-xl h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700 dark:text-gray-300 text-sm font-medium">
                          {t('settings.security.confirmPassword')}
                        </Label>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-[#16c47f] focus:ring-[#16c47f]/20 rounded-xl h-11"
                        />
                      </div>
                      {passwordError && <p className="text-red-500 text-sm">{passwordError}</p>}
                      <Button
                        className="mt-4 bg-[#16c47f] hover:bg-[#12b070] text-white font-medium"
                        onClick={handlePasswordChange}
                        disabled={passwordLoading}
                      >
                        {passwordLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {t('settings.security.updatePassword')}
                      </Button>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <Smartphone className="w-5 h-5 text-[#16c47f]" />
                      {t('settings.security.twoFactor')}
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-5">
                      {t('settings.security.twoFactorDesc')}
                    </p>
                    <div className="flex items-center gap-3">
                      {has2FAEnabled && (
                        <span className="px-3 py-1 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-xs font-medium rounded-full flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          {t('settings.security.2faActive') || 'Active'}
                        </span>
                      )}
                      <Button
                        variant="outline"
                        className="border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                        onClick={() => setIs2FAModalOpen(true)}
                      >
                        {has2FAEnabled
                          ? t('settings.security.manage2FA') || 'Manage 2FA'
                          : t('settings.security.enable2FA')}
                      </Button>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                    <div className="flex items-center justify-between mb-5">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {t('settings.security.activeSessions')}
                      </h2>
                      {sessions.length > 1 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30"
                          onClick={handleSignOutOthers}
                        >
                          <LogOut className="w-4 h-4 mr-2" />
                          {t('settings.security.signOutOthers')}
                        </Button>
                      )}
                    </div>
                    {sessionsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 text-[#16c47f] animate-spin" />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {sessions.map((session) => {
                          const DeviceIcon = getDeviceIcon(session.user_agent);
                          const { browser, os } = parseUserAgent(session.user_agent);
                          return (
                            <div
                              key={session.id}
                              className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-700"
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center">
                                  <DeviceIcon className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  <p className="text-gray-900 dark:text-white font-medium text-sm">
                                    {browser} {t('settings.security.onDevice')} {os}
                                  </p>
                                  <p className="text-gray-400 dark:text-gray-500 text-xs">
                                    {session.is_current
                                      ? t('settings.security.activeNow')
                                      : new Date(session.updated_at).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              {session.is_current ? (
                                <span className="px-3 py-1 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-xs font-medium rounded-full">
                                  {t('settings.security.currentSession')}
                                </span>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-700"
                                  onClick={() => handleRevokeSession(session.id)}
                                  disabled={revokingSessionId === session.id}
                                >
                                  {revokingSessionId === session.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <LogOut className="w-4 h-4 mr-1" />
                                      {t('settings.security.revoke')}
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Appearance Tab */}
              {activeTab === 'appearance' && (
                <div className="space-y-6">
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-5 flex items-center gap-2">
                      <Palette className="w-5 h-5 text-[#16c47f]" />
                      {t('settings.appearance.theme')}
                    </h2>
                    <div className="grid md:grid-cols-2 gap-4">
                      <button
                        onClick={() => setDarkMode(true)}
                        className={`p-5 rounded-xl border-2 transition-all duration-200 ${
                          isDarkMode
                            ? 'border-[#16c47f] bg-[#16c47f]/10'
                            : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <Moon className="w-7 h-7 text-gray-700 dark:text-gray-300 mb-3" />
                        <p className="text-gray-900 dark:text-white font-semibold text-sm">
                          {t('settings.appearance.darkMode')}
                        </p>
                        <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                          {t('settings.appearance.darkModeDesc')}
                        </p>
                      </button>
                      <button
                        onClick={() => setDarkMode(false)}
                        className={`p-5 rounded-xl border-2 transition-all duration-200 ${
                          !isDarkMode
                            ? 'border-[#16c47f] bg-[#16c47f]/10'
                            : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <Sun className="w-7 h-7 text-gray-700 dark:text-gray-300 mb-3" />
                        <p className="text-gray-900 dark:text-white font-semibold text-sm">
                          {t('settings.appearance.lightMode')}
                        </p>
                        <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                          {t('settings.appearance.lightModeDesc')}
                        </p>
                      </button>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <Globe className="w-5 h-5 text-[#16c47f]" />
                      {t('settings.appearance.language')}
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                      {t('settings.appearance.languageDesc')}
                    </p>
                    <LanguageSwitcher variant="cards" />
                  </div>
                </div>
              )}

              {/* Currency Tab */}
              {activeTab === 'currency' && (
                <div className="space-y-6">
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <Coins className="w-5 h-5 text-[#16c47f]" />
                      {t('settings.appearance.currency')}
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                      {t('settings.appearance.currencyDesc')}
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      {(Object.keys(CURRENCIES) as Currency[]).map((curr) => {
                        const config = CURRENCIES[curr];
                        return (
                          <button
                            key={curr}
                            onClick={() => setCurrency(curr)}
                            className={`p-5 rounded-xl border-2 transition-all duration-200 text-left ${
                              currency === curr
                                ? 'border-[#16c47f] bg-[#16c47f]/10'
                                : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
                            }`}
                          >
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-2xl font-bold text-[#16c47f]">
                                {config.symbol}
                              </span>
                              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                {config.code}
                              </span>
                            </div>
                            <p className="text-gray-700 dark:text-gray-300 font-medium text-sm">
                              {language === 'fr' ? config.nameFr : config.name}
                            </p>
                            <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                              {t('settings.appearance.currencyExample')}: 1 234,56 {config.symbol}
                            </p>
                          </button>
                        );
                      })}
                    </div>

                    {/* Exchange Rates Table */}
                    {exchangeRates.length > 0 && (
                      <div className="mt-6 bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                          {language === 'fr' ? 'Taux de change du jour' : "Today's exchange rates"}
                        </h3>
                        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-100 dark:bg-gray-700">
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                  {language === 'fr' ? 'De' : 'From'}
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                  {language === 'fr' ? 'Vers' : 'To'}
                                </th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                  {language === 'fr' ? 'Taux' : 'Rate'}
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                              {exchangeRates
                                .filter((r) => r.base_currency === currency)
                                .map((rate) => (
                                  <tr
                                    key={`${rate.base_currency}-${rate.target_currency}`}
                                    className="bg-white dark:bg-gray-800"
                                  >
                                    <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">
                                      1{' '}
                                      {CURRENCIES[rate.base_currency as Currency]?.symbol ||
                                        rate.base_currency}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300">
                                      {CURRENCIES[rate.target_currency as Currency]?.symbol ||
                                        rate.target_currency}{' '}
                                      {rate.target_currency}
                                    </td>
                                    <td className="px-3 py-2 text-right text-gray-900 dark:text-white font-mono tabular-nums">
                                      {rate.rate.toFixed(4)}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                        {ratesUpdatedAt && (
                          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
                            {language === 'fr'
                              ? `Mis à jour le ${new Date(ratesUpdatedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })} à ${new Date(ratesUpdatedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
                              : `Updated on ${new Date(ratesUpdatedAt).toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })} at ${new Date(ratesUpdatedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Billing Tab */}
              {activeTab === 'billing' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-[#16c47f]/10 to-emerald-50 dark:from-[#16c47f]/20 dark:to-emerald-900/20 rounded-2xl border border-[#16c47f]/20 p-6">
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                          {t('settings.billing.currentPlan')}
                        </h2>
                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                          {t('settings.billing.planDesc')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-gray-900 dark:text-white">
                          0 {t('common.currency')}
                        </p>
                        <p className="text-gray-500 dark:text-gray-400 text-sm">
                          {t('settings.billing.perMonth')}
                        </p>
                      </div>
                    </div>
                    <Button className="w-full bg-[#16c47f] hover:bg-[#12b070] text-white font-semibold">
                      <Zap className="w-4 h-4 mr-2" />
                      {t('settings.billing.upgradePremium')}
                    </Button>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">
                      {t('settings.billing.features')}
                    </h2>
                    <div className="grid md:grid-cols-2 gap-3">
                      {[
                        t('settings.billing.feature1'),
                        t('settings.billing.feature2'),
                        t('settings.billing.feature3'),
                        t('settings.billing.feature4'),
                        t('settings.billing.feature5'),
                        t('settings.billing.feature6'),
                      ].map((feature, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-700"
                        >
                          <div className="w-7 h-7 bg-green-100 dark:bg-green-900/40 rounded-lg flex items-center justify-center">
                            <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                          </div>
                          <span className="text-gray-700 dark:text-gray-300 text-sm">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <Download className="w-5 h-5 text-[#16c47f]" />
                      {t('settings.billing.exportData')}
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-5">
                      {t('settings.billing.exportDesc')}
                    </p>
                    <div className="flex gap-3">
                      <button className="inline-flex items-center px-5 py-2.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium text-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-all">
                        <Download className="w-4 h-4 mr-2" />
                        {t('settings.billing.exportCSV')}
                      </button>
                      <button className="inline-flex items-center px-5 py-2.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium text-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-all">
                        <Download className="w-4 h-4 mr-2" />
                        {t('settings.billing.exportJSON')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Account Management Tab */}
              {activeTab === 'account' && (
                <div className="space-y-6">
                  {/* Data Retention Info */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-[#16c47f]" />
                      {t('settings.account.dataRetention')}
                    </h2>
                    <div className="space-y-3">
                      {[
                        {
                          label: t('settings.account.dataFinancial'),
                          value: t('settings.account.dataFinancialValue'),
                        },
                        {
                          label: t('settings.account.dataConnection'),
                          value: t('settings.account.dataConnectionValue'),
                        },
                        {
                          label: t('settings.account.dataCookies'),
                          value: t('settings.account.dataCookiesValue'),
                        },
                        {
                          label: t('settings.account.dataAfterDelete'),
                          value: t('settings.account.dataAfterDeleteValue'),
                        },
                      ].map((item, i) => (
                        <div
                          key={i}
                          className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-[#16c47f] mt-2 flex-shrink-0" />
                          <div>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {item.label}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">
                              — {item.value}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Delete Account */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-red-200 dark:border-red-900/50 p-6">
                    <h2 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      {t('settings.account.dangerZone')}
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-2">
                      {t('settings.account.deleteWarning')}
                    </p>
                    <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1 mb-5 ml-4 list-disc">
                      <li>{t('settings.account.deleteInfo1')}</li>
                      <li>{t('settings.account.deleteInfo2')}</li>
                      <li>{t('settings.account.deleteInfo3')}</li>
                    </ul>

                    {!showDeleteConfirm ? (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="inline-flex items-center px-5 py-2.5 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-xl font-medium text-sm transition-all"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {t('settings.account.deleteBtn')}
                      </button>
                    ) : (
                      <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 space-y-4">
                        <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                          {t('settings.account.deleteConfirmLabel')}
                        </p>
                        <Input
                          value={deleteConfirmText}
                          onChange={(e) => setDeleteConfirmText(e.target.value)}
                          placeholder="SUPPRIMER"
                          className="border-red-300 dark:border-red-700 focus:ring-red-500"
                        />
                        <div className="flex gap-3">
                          <button
                            onClick={handleDeleteAccount}
                            disabled={deleteConfirmText !== 'SUPPRIMER' || accountActionLoading}
                            className="inline-flex items-center px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-medium text-sm transition-all"
                          >
                            {accountActionLoading ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4 mr-2" />
                            )}
                            {t('settings.account.confirmDelete')}
                          </button>
                          <button
                            onClick={() => {
                              setShowDeleteConfirm(false);
                              setDeleteConfirmText('');
                            }}
                            className="px-5 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium text-sm transition-all"
                          >
                            {t('settings.account.cancel')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Achievements Tab */}
              {activeTab === 'achievements' && <AchievementsSettings />}
            </div>
          </div>
        </div>

        {/* 2FA Setup Modal */}
        <TwoFactorSetupModal
          isOpen={is2FAModalOpen}
          onClose={() => setIs2FAModalOpen(false)}
          onSuccess={async () => {
            const { data } = await supabase.auth.mfa.listFactors();
            const verifiedFactors = data?.totp.filter((f) => f.status === 'verified') || [];
            setHas2FAEnabled(verifiedFactors.length > 0);
          }}
        />

        <DisableBankSyncModal
          open={isDisablingBankSync}
          syncedAccountCount={syncedAccounts.length}
          onClose={() => setIsDisablingBankSync(false)}
          onDowngraded={() => loadAccounts()}
        />
      </main>
    </div>
  );
}
